import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PopUp from "src/components/common/PopUp";
import MapComponent from "src/components/common/KML";
import { fileUpload } from "src/utils/api/fileupload";
import {
  getNewKmlThread,
  NewKmlThread,
  submitNewKml,
} from "src/utils/api/newKml";

type AddKmlModalProps = {
  open: boolean;
  epid: string;
  onClose: () => void;
  onSuccess: () => void;
};

const statusLabel: Record<string, string> = {
  PENDING_REVIEW: "Pending Admin Review",
  UNDER_QUERY: "Admin Raised a Query",
  APPROVED: "Approved (locked)",
};

const AddKmlModal = ({ open, epid, onClose, onSuccess }: AddKmlModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [thread, setThread] = useState<NewKmlThread | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);

  const [fileKey, setFileKey] = useState("");
  const [kmlUrl, setKmlUrl] = useState("");
  const [filename, setFilename] = useState("");
  const [comment, setComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLocked = thread?.isLocked ?? false;
  const hasExisting = !!thread?.newKml?.url;
  // IO may submit only for the first upload, or when the admin has raised a
  // query. Not while it is pending review or already approved.
  const canSubmit =
    !thread?.newKmlStatus || thread.newKmlStatus === "UNDER_QUERY";
  // Prefer the just-uploaded file so it renders on the map immediately,
  // otherwise fall back to the latest submitted KML.
  const displayKmlUrl = kmlUrl || thread?.newKml?.url || "";

  const resetForm = () => {
    setFileKey("");
    setKmlUrl("");
    setFilename("");
    setComment("");
    setUploading(false);
    setSubmitting(false);
    setError(null);
  };

  useEffect(() => {
    if (!open) return;
    resetForm();
    setLoadingThread(true);
    getNewKmlThread(epid)
      .then((res) => setThread(res.data))
      .catch(() => setThread(null))
      .finally(() => setLoadingThread(false));
  }, [open, epid]);

  const handleClose = () => {
    if (uploading || submitting) return;
    resetForm();
    onClose();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setError(null);
    setUploading(true);
    setFilename(file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fileUpload(fd as any);
      const uploaded = res?.data?.data?.uploadedFile;
      if (!uploaded?.key) throw new Error("Upload failed");
      setFileKey(uploaded.key);
      setKmlUrl(uploaded.fileUrl);
    } catch {
      setError("Failed to upload KML file. Please try again.");
      setFilename("");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!fileKey) {
      setError("Please upload a KML file.");
      return;
    }
    if (!comment.trim()) {
      setError("Please add a comment.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await submitNewKml(epid, { fileKey, comment: comment.trim() });
      resetForm();
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          "Failed to submit new KML. Please try again."
      );
      setSubmitting(false);
    }
  };

  return (
    <PopUp
      open={open}
      handleClose={handleClose}
      heading="New KML"
      loading={submitting}
      loadingText="Submitting…"
      actionLeft={{ label: "Close", onClick: handleClose }}
      actionRight={
        canSubmit
          ? { label: hasExisting ? "Replace KML" : "Submit" }
          : { hidden: true }
      }
      handleRightBtnClick={handleSubmit}
      customStyles={{
        wrapper: { maxWidth: "900px", width: "92vw" },
        actionButtons: { gap: "12px" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxHeight: "70vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
            New KML
          </Typography>
          {thread?.newKmlStatus && (
            <Chip
              size="small"
              color={isLocked ? "success" : "warning"}
              label={statusLabel[thread.newKmlStatus] ?? thread.newKmlStatus}
            />
          )}
        </Box>

        {loadingThread ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isLocked && (
              <Typography variant="body2" color="success.main">
                This KML has been approved by the admin and can no longer be
                changed.
              </Typography>
            )}

            {displayKmlUrl && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {kmlUrl ? "New KML Preview" : "Latest Submitted KML"}
                </Typography>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: kmlUrl ? "success.light" : "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <MapComponent key={displayKmlUrl} kmlUrl={displayKmlUrl} />
                </Box>
              </Box>
            )}

            {!isLocked && !canSubmit && (
              <Typography variant="body2" color="warning.main">
                Your KML has been submitted and is awaiting admin review. You
                can upload a new KML once the admin raises a query.
              </Typography>
            )}

            {canSubmit && (
              <>
                <Divider />
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {hasExisting ? "Replace KML File" : "KML File"}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Box
                    onClick={() => !uploading && inputRef.current?.click()}
                    sx={{
                      border: "1px dashed",
                      borderColor:
                        error && !fileKey ? "error.main" : "primary.main",
                      borderRadius: 1,
                      px: 2,
                      py: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: uploading ? "default" : "pointer",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flex: 1,
                      }}
                    >
                      {uploading ? (
                        <>
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            Uploading…
                          </Typography>
                        </>
                      ) : fileKey ? (
                        <>
                          <CheckCircleOutlineIcon
                            color="success"
                            fontSize="small"
                          />
                          <Typography
                            variant="body2"
                            sx={{ wordBreak: "break-all" }}
                          >
                            {filename}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <UploadFileIcon color="disabled" fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            No KML file selected
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={uploading}
                      onClick={(e) => {
                        e.stopPropagation();
                        inputRef.current?.click();
                      }}
                      startIcon={<UploadFileIcon />}
                    >
                      {fileKey ? "Change File" : "Upload File"}
                    </Button>
                    <input
                      ref={inputRef}
                      type="file"
                      accept=".kml,application/vnd.google-earth.kml+xml"
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                  </Box>
                </Box>
              </>
            )}

            {!!thread?.thread?.length && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Comments
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {thread.thread.map((c) => (
                    <Box
                      key={c.id}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {c.authorUserName || c.authorSubType}{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          ({c.authorSubType})
                        </Typography>
                      </Typography>
                      <Typography variant="body2">{c.comment}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {canSubmit && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                  Comment <span style={{ color: "red" }}>*</span>
                </Typography>
                <Box
                  component="textarea"
                  rows={3}
                  value={comment}
                  onChange={(e: any) => setComment(e.target.value)}
                  placeholder="Add a note about this KML…"
                  sx={{
                    width: "100%",
                    boxSizing: "border-box",
                    p: 1.2,
                    fontFamily: "inherit",
                    fontSize: "14px",
                    color: "text.primary",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    resize: "vertical",
                    "&::placeholder": { color: "text.secondary" },
                    "&:focus": {
                      outline: "none",
                      borderColor: "primary.main",
                    },
                  }}
                />
              </Box>
            )}

            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
          </>
        )}
      </Box>
    </PopUp>
  );
};

export default AddKmlModal;
